// 模式切换 / Mode toggle feature.
// 控制 Single / A/B 两种仿真模式的切换，并用 cookie 记住用户最近选择。
// Toggles Single / A/B simulation modes and persists the user's latest choice.

import { $maybe } from "@/lib/dom";
import { readCookie, writeCookie } from "@/lib/cookies";
import { state } from "@/state/store";
import type { Mode } from "@/lib/types";

const MODE_COOKIE = "ripple_pmf_mode";

interface SetModeOptions {
  persist?: boolean;
}

let modeToggleEvents: AbortController | null = null;

/**
 * 切换模式并更新相关 DOM / Set the active mode and sync dependent UI.
 * @param mode "single" | "ab"
 */
export function setMode(mode: Mode, options: SetModeOptions = {}): void {
  const nextMode = mode === "ab" ? "ab" : "single";
  const changed = state.mode !== nextMode;
  state.mode = nextMode;
  syncModeButtons();
  if (options.persist) {
    writeCookie(MODE_COOKIE, state.mode);
  }
  if (changed) {
    window.dispatchEvent(
      new CustomEvent("ripple:mode-changed", { detail: { mode: state.mode } }),
    );
  }
}

// 同步按钮选中态 / Sync mode-button selected state.
function syncModeButtons(): void {
  const singleBtn = $maybe<HTMLButtonElement>("mode-single");
  const abBtn = $maybe<HTMLButtonElement>("mode-ab");
  if (singleBtn) {
    singleBtn.classList.toggle("active", state.mode === "single");
    singleBtn.setAttribute("aria-selected", String(state.mode === "single"));
  }
  if (abBtn) {
    abBtn.classList.toggle("active", state.mode === "ab");
    abBtn.setAttribute("aria-selected", String(state.mode === "ab"));
  }
}

/**
 * 读取已保存的模式 / Read the saved mode cookie.
 */
function readInitialMode(): Mode {
  return readCookie(MODE_COOKIE) === "ab" ? "ab" : "single";
}

/**
 * 绑定模式切换按钮 / Wire mode-toggle click handlers.
 */
export function initModeToggle(): void {
  modeToggleEvents?.abort();
  modeToggleEvents = new AbortController();
  const { signal } = modeToggleEvents;
  const single = $maybe<HTMLButtonElement>("mode-single");
  const ab = $maybe<HTMLButtonElement>("mode-ab");
  single?.addEventListener("click", () => setMode("single", { persist: true }), { signal });
  ab?.addEventListener("click", () => setMode("ab", { persist: true }), { signal });
  setMode(readInitialMode());
}
